import { usePage } from 'modules/page/pageSelector';
import { MODULE_STATUS } from 'constants/modules';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageData } from 'types/Page';
import {
  ActivityIndicator,
  Alert,
  BrandLogo,
  Button,
  Icon,
  SocialMediaIcon,
  SocialMediaIconProps,
} from '@kontentino/ui';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import PageTypeUtils from 'app/utils/pageType';
import { addQueryParams } from 'utils/url';
import { modalRoutes } from 'constants/routes';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';

function groupPagesByType(pages: PageData[]) {
  const grouped = pages
    .filter(
      (page) =>
        !page.isConnected && !page.isDemo && !page.isGlobal && !page.isBlank,
    )
    .reduce((acc: { [key: number]: PageData[] }, item: PageData) => {
      if (!acc[item.type]) {
        acc[item.type] = [];
      }
      acc[item.type].push(item);
      return acc;
    }, {});

  return Object.entries(grouped).map(([type, data]) => ({
    value: Number(type),
    data,
  }));
}

type Props = {
  onClose: () => void;
};

const DisconnectedProfilesList: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const socialLogin = useSocialLoginContext();
  const [expandedTypes, setExpandedTypes] = useState<Set<number>>(new Set());

  const { pages, status } = usePage();

  if (status === MODULE_STATUS.Loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-6">
        <ActivityIndicator />
      </div>
    );
  }

  if (groupPagesByType(pages).length === 0) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-5 tw-py-8">
        <Alert
          cta={{
            label: t('close'),
            onClick: onClose,
            position: 'right',
          }}
        >
          {t('disconnectedProfilesModal.emptyDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-5 tw-py-6">
      {groupPagesByType(pages).map((option) => {
        const isExpanded = expandedTypes.has(option.value);

        return (
          <div key={option.value} className="tw-flex tw-flex-col">
            <div className="tw-flex tw-items-center tw-justify-between">
              <button
                type="button"
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-none tw-bg-transparent tw-p-0"
                onClick={() => {
                  setExpandedTypes((prev) => {
                    const next = new Set(prev);

                    if (next.has(option.value)) {
                      next.delete(option.value);
                    } else {
                      next.add(option.value);
                    }

                    return next;
                  });
                }}
              >
                <Icon
                  icon={faChevronDown}
                  size="sm"
                  className={`tw-transition-transform tw-duration-200 ${
                    isExpanded ? 'tw-rotate-180' : ''
                  }`}
                />
                <SocialMediaIcon
                  size={24}
                  type={
                    PageTypeUtils.getNameFromNumber(
                      option.value,
                    ) as SocialMediaIconProps['type']
                  }
                />
                <span className="tw-line-clamp-1 tw-overflow-hidden tw-text-ellipsis tw-break-all tw-text-md tw-font-medium">
                  {t('disconnectedProfilesModal.option', {
                    count: option.data.length,
                  })}
                </span>
              </button>
              <Button
                variant="primary"
                size="small"
                iconBefore={<Icon icon={faArrowsRotate} size="sm" />}
                onClick={() => {
                  socialLogin.authorize({
                    pageType: option.value,
                    redirectUrl: `${addQueryParams(
                      window.location.href.replace(window.location.hash, ''),
                      {
                        type: option.value,
                        nextHash: modalRoutes.DISCONNECTED_PROFILES,
                      },
                    )}#${modalRoutes.ASSOCIATE_POSTS_PROFILE}`,
                  });
                }}
                data-name="profile-selector_reconnect-profile-button"
              >
                {t('reconnect')}
              </Button>
            </div>
            {isExpanded && (
              <div className="tw-ml-[54px] tw-mt-2 tw-flex tw-flex-col tw-gap-2">
                {option.data.map((page) => (
                  <div
                    key={page.id}
                    className="tw-flex tw-items-center tw-gap-2"
                  >
                    <BrandLogo
                      size={22}
                      src={page.logo?.src}
                      name={page.name}
                      socialMediaType={getSocialMediaTypeForBrandLogo(
                        page.type,
                      )}
                    />
                    <span className="tw-text-gray-600 tw-text-sm">
                      {page.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DisconnectedProfilesList;
